import { and, eq, getTableColumns, sql } from "drizzle-orm";

import type { WsPermission } from "@storyboard/web-client";
import { bulkInsert } from "@/db/bulkInsert";
import { db } from "@/db/db";
import { permission, workspaceMembership } from "@/db/schema";
import { deduplicateByKeyValue } from "@/models/appSyncFunctions.ts";
import { emptyQuery } from "@/models/emptyQuery.ts";
import { emitTableUpdates } from "@/models/eventing/TableEventManager";
import { logQuery } from "@/models/logQuery.ts";

function transformWsPermission(r: WsPermission) {
  return {
    id: r.id,
    workspaceMembershipId: r.workspaceMembershipId,
    feedId: r.feedId,
    workflowItemId: r.workflowItemId,
    name: r.name,
    enabled: r.enabled,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
  };
}

export async function bulkUpsertPermission(records: WsPermission[]) {
  if (records.length === 0) return;
  const data = records.map((p) => transformWsPermission(p));
  const deduped = deduplicateByKeyValue(data, (item) => item.id);
  await bulkInsert(permission, deduped, {
    onConflict: {
      type: "doUpdate",
      target: [permission.id],
      set: {
        workspaceMembershipId: sql`"excluded"."workspaceMembershipId"`,
        feedId: sql`"excluded"."feedId"`,
        workflowItemId: sql`"excluded"."workflowItemId"`,
        name: sql`"excluded"."name"`,
        enabled: sql`"excluded"."enabled"`,
        updatedAt: sql`"excluded"."updatedAt"`,
      },
    },
  });
  emitTableUpdates(["permission"]);
}

export function myFeedPermissionsQuery({
  currentFeedId,
  myCurrentWorkspaceMembershipId,
}: {
  currentFeedId?: string;
  myCurrentWorkspaceMembershipId?: string;
}) {
  const query = db
    .select()
    .from(permission)
    .where(
      and(
        eq(permission.feedId, currentFeedId),
        eq(permission.workspaceMembershipId, myCurrentWorkspaceMembershipId),
        eq(permission.enabled, true),
      ),
    );
  if (!currentFeedId || !myCurrentWorkspaceMembershipId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("My Feed Permissions", query);
  return query;
}

export function feedPermissionsQuery({ feedId }: { feedId?: string }) {
  const query = db
    .select({
      ...getTableColumns(permission),
    })
    .from(permission)
    .innerJoin(
      workspaceMembership,
      and(eq(permission.workspaceMembershipId, workspaceMembership.id), eq(workspaceMembership.status, "active")),
    )
    .where(and(eq(permission.feedId, feedId)));

  if (!feedId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Feed Permissions", query);
  return query;
}

export const feedPermissions = (feedId: string) => {
  const query = db.select({ id: permission.id }).from(permission).where(eq(permission.feedId, feedId)).limit(10);
  if (!feedId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Feed Permissions", query);
  return query;
};
export const fetchFeedPermissions = ({
  workspaceMembershipId,
  feedId,
}: {
  workspaceMembershipId: string;
  feedId: string;
}) => {
  const query = db
    .select({
      name: permission.name,
      enabled: permission.enabled,
    })
    .from(permission)
    .where(and(eq(permission.workspaceMembershipId, workspaceMembershipId), eq(permission.feedId, feedId)))
    .limit(3);

  if (!workspaceMembershipId || !feedId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Fetch Feed Permissions", query);
  return query;
};
